import { FileQuestion, ShieldOff } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Container } from "../../components/common/HtmlTags/Container/Container";
import { Heading } from "../../components/common/HtmlTags/Heading/Heading";
import { Paragraph } from "../../components/common/HtmlTags/Paragraph/Paragraph";
import { Button } from "../../components/common/HtmlTags/Button/Button";
import { SHARE_LABELS, NOT_FOUND_VARIANT } from "../../utils/constants";
import "./NotFoundPage.css";

type NotFoundVariant = (typeof NOT_FOUND_VARIANT)[keyof typeof NOT_FOUND_VARIANT];

const VARIANT_CONFIG: Record<
  (typeof NOT_FOUND_VARIANT)[keyof typeof NOT_FOUND_VARIANT],
  { title: string; description: string; Icon: typeof FileQuestion }
> = {
  [NOT_FOUND_VARIANT.PAGE_NOT_FOUND]: {
    title: SHARE_LABELS.PAGE_NOT_FOUND_TITLE,
    description: SHARE_LABELS.PAGE_NOT_FOUND_DESCRIPTION,
    Icon: FileQuestion,
  },
  [NOT_FOUND_VARIANT.ACCESS_DENIED]: {
    title: SHARE_LABELS.ACCESS_DENIED_TITLE,
    description: SHARE_LABELS.ACCESS_DENIED_DESCRIPTION,
    Icon: ShieldOff,
  },
};

const isNotFoundVariant = (value: string | null): value is NotFoundVariant =>
  value === "page-not-found" || value === "access-denied";

interface NotFoundPageProps {
  variant?: NotFoundVariant;
}

const NotFoundPage = ({ variant: variantProp }: NotFoundPageProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const variantParam = searchParams.get("variant");
  const variant: NotFoundVariant =
    variantProp ??
    (isNotFoundVariant(variantParam) ? variantParam : NOT_FOUND_VARIANT.PAGE_NOT_FOUND);

  const { title, description, Icon } = VARIANT_CONFIG[variant];

  return (
    <Container className="not-found-wrapper">
      <Container className="not-found-content-section-wrapper">
        <Icon
          className="not-found-icon"
          strokeWidth={1.5}
          color="var(--primary-shade1)"
        />
        <Container className="not-found-content-wrapper">
          <Heading variant="h2">{title}</Heading>
          <Paragraph>{description}</Paragraph>
          <Button
            className="not-found-home-btn"
            type="button"
            palette="primary"
            size="large"
            colorScheme="dark"
            hasIcon={false}
            onClick={() => navigate("/")}
          >
            {SHARE_LABELS.GO_TO_HOME}
          </Button>
        </Container>
      </Container>
    </Container>
  );
};

export default NotFoundPage;
